import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import en from "@/i18n/hot-wallets-locales/en.json";
import type { HotWalletsLocaleCatalog } from "@/i18n/use-hot-wallets-locale";

/** The default-locale catalogue (`DEFAULT_HOT_WALLETS_LOCALE`), used only as a
 * last resort when the active catalog is missing one of the five `nav.*` keys. */
const DEFAULT_UI: Record<string, string> = en.ui;

/**
 * Route-level tab bar for a single hot wallet's sub-pages (WIREFRAMES.md
 * HW-S1: "Tabs: Overview | Addresses & deposits | Withdrawals |
 * Reconciliation | Console"). Real <NavLink>s between routes, not the
 * Radix content-switching Tabs used elsewhere in this app for same-page
 * panels — each of these tabs is its own route with its own data.
 *
 * The Withdrawals tab intentionally links to the existing `/approvals`
 * page rather than a new `/hot-wallets/:id/withdrawals` route: SPEC-HW-8 §1
 * says withdrawals reuse `approvals`/`approval-detail` (HW-S3, a separate,
 * owned lane), and this lane must not add or touch that route.
 */
export function HotWalletTabs({
  walletId,
  active,
  labels,
}: {
  walletId: string;
  active: "overview" | "deposits" | "reconciliation" | "console";
  labels: HotWalletsLocaleCatalog["ui"];
}) {
  // `labels` arrives as a plain record straight from a locale catalogue, so at
  // runtime a key can be missing or an empty string (a partially translated
  // catalog). Resolve each label through the default-locale catalogue so a tab
  // is never an unlabelled blank and the nav landmark is never nameless — the
  // honest outcome for an unknown label is the English string, and only a key
  // absent from every catalogue falls back to the key itself rather than "".
  const t = (key: string) => labels[key] || DEFAULT_UI[key] || key;

  // `shrink-0 whitespace-nowrap` mirror the shared `TabsTrigger` classes: the
  // five labels are wider than a 375px viewport, so each tab keeps its own
  // width and the nav scrolls horizontally instead of stretching the page.
  const tabClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "shrink-0 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors",
      isActive ? "border-primary text-primary" : "border-transparent text-muted hover:text-ink",
    );

  return (
    // `overflow-x-auto` turns the tab strip into its own horizontal scroller
    // (the shared `TabsList` does the same), so the five routes scroll under a
    // thumb at 375px rather than pushing the whole page wider than the
    // viewport. The row never wraps — a wrapped tab bar reads as two rows of
    // navigation, not one strip.
    <nav aria-label={t("nav.overview")} className="mb-5 flex overflow-x-auto border-b border-line">
      <NavLink to={`/hot-wallets/${walletId}`} end className={tabClass} aria-current={active === "overview" ? "page" : undefined}>
        {t("nav.overview")}
      </NavLink>
      <NavLink to={`/hot-wallets/${walletId}/deposits`} className={tabClass} aria-current={active === "deposits" ? "page" : undefined}>
        {t("nav.deposits")}
      </NavLink>
      <NavLink to="/approvals" className={tabClass}>
        {t("nav.withdrawals")}
      </NavLink>
      <NavLink to={`/hot-wallets/${walletId}/reconciliation`} className={tabClass} aria-current={active === "reconciliation" ? "page" : undefined}>
        {t("nav.reconciliation")}
      </NavLink>
      <NavLink to="/developer/hot-wallets" className={tabClass} aria-current={active === "console" ? "page" : undefined}>
        {t("nav.console")}
      </NavLink>
    </nav>
  );
}
